import React, { useEffect, useState } from 'react';
import Layout from '../components/layout/Layout';
import LoadingSpinner from '../components/common/LoadingSpinner';
import { useAuth } from '../contexts/AuthContext';
import { getBranches, BranchInfo } from '../api/franchise.api';
import styles from './BranchesPage.module.css';

function formatPhone(phone: string): string {
  if (!phone) return '-';
  if (phone.length === 11) {
    return `${phone.slice(0, 3)}-${phone.slice(3, 7)}-${phone.slice(7)}`;
  }
  if (phone.length === 10) {
    return `${phone.slice(0, 3)}-${phone.slice(3, 6)}-${phone.slice(6)}`;
  }
  return phone;
}

function getStatusBadge(status: string): { label: string; className: string } {
  switch (status?.toUpperCase()) {
    case 'ACTIVE':
      return { label: '활성', className: styles.badgeActive };
    case 'EXPIRED_CLOSED':
    case 'DEAD':
      return { label: '만료', className: styles.badgeExpired };
    case 'SUSPENDED':
      return { label: '정지', className: styles.badgeSuspended };
    case 'PENDING_CLOSED':
      return { label: '폐업유예', className: styles.badgeExpired };
    default:
      return { label: status || '-', className: styles.badgeDefault };
  }
}

export default function BranchesPage() {
  const { franchiseId } = useAuth();
  const [branches, setBranches] = useState<BranchInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!franchiseId) return;
    (async () => {
      try {
        const data = await getBranches(franchiseId);
        setBranches(data);
      } catch (err) {
        setError(err instanceof Error ? err.message : '데이터 조회 실패');
      } finally {
        setLoading(false);
      }
    })();
  }, [franchiseId]);

  if (loading) {
    return (
      <Layout title="지점 관리">
        <LoadingSpinner />
      </Layout>
    );
  }

  if (error) {
    return (
      <Layout title="지점 관리">
        <div className={styles.error}>{error}</div>
      </Layout>
    );
  }

  return (
    <Layout title="지점 관리">
      <div className={styles.tableWrapper}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th>지점명</th>
              <th>전화번호</th>
              <th>주소</th>
              <th>라이선스 상태</th>
              <th>라이선스 키</th>
            </tr>
          </thead>
          <tbody>
            {branches.length === 0 ? (
              <tr>
                <td colSpan={5} className={styles.empty}>
                  등록된 지점이 없습니다.
                </td>
              </tr>
            ) : (
              branches.map((b) => {
                const badge = getStatusBadge(b.licenseStatus);
                return (
                  <tr key={b.storeId}>
                    <td className={styles.name}>{b.storeName}</td>
                    <td>{formatPhone(b.phone)}</td>
                    <td>{b.address || '-'}</td>
                    <td>
                      <span className={`${styles.badge} ${badge.className}`}>
                        {badge.label}
                      </span>
                    </td>
                    <td className={styles.licenseKey}>{b.licenseKey || '-'}</td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </Layout>
  );
}
